import MenuButton from "@components/ui/buttons/MenuButton";
import SideMenuButton from "@navigation/SideMenuButton";
import Icon1 from "@assets/icons/Icon";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { ORDRE_FLAG_CONFIG } from "types/ordre";
import type { Ordre, OrdreFlag } from "types/ordre";
import type { Vehicle } from "types/garage/vehicules";
import type { Payment } from "types/payment";

const MOBILITY_LABELS: Record<string, string> = {
  attente: "Attente sur place",
  velo: "Vélo",
  pret: "Véhicule de prêt",
  location: "Véhicule de location",
};

/**
 * Shared "Actions" menu for an ordre, used both on the Ordre detail page and in
 * the OrdreModal (opened from the planning). The menu markup is the single
 * source of truth so both stay identical; each host renders the sub-modals the
 * actions open and passes the corresponding callbacks.
 *
 * `context="modal"` hides actions that are redundant/page-only when the ordre is
 * already open as a modal from the planning (edit, navigate to this planning).
 */
export interface OrdreActionsMenuProps {
  ordre: Ordre;
  orgData: any;
  context?: "page" | "modal";
  closeRef?: React.MutableRefObject<(() => void) | null>;
  payments: Payment[];
  rentalVehicle: Vehicle | null;
  hasFacture: boolean;
  serviceboxProxyAvailable: boolean;
  sbEstimationLoading: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRequestFlagReason: (flag: OrdreFlag, newFlags: OrdreFlag[]) => void;
  onShowReminder: () => void;
  onAddDocument: () => void;
  onAddFacture: () => void;
  onServiceBoxTransfer: () => void;
  onImportEstimation: () => void;
  onShowSecondary: () => void;
  onShowRental: () => void;
  onChangeRental: () => void;
  onRemoveRental: () => void;
  onEditReservation?: () => void;
  onShowContrat: () => void;
  onShowTracking: () => void;
  onShowPayment: (type: "acompte" | "solde") => void;
  onRecordManualPayment?: () => void;
}

export default function OrdreActionsMenu({
  ordre,
  orgData,
  context = "page",
  closeRef,
  payments,
  rentalVehicle,
  hasFacture,
  serviceboxProxyAvailable,
  sbEstimationLoading,
  onEdit,
  onDelete,
  onRequestFlagReason,
  onShowReminder,
  onAddDocument,
  onAddFacture,
  onServiceBoxTransfer,
  onImportEstimation,
  onShowSecondary,
  onShowRental,
  onChangeRental,
  onRemoveRental,
  onEditReservation,
  onShowContrat,
  onShowTracking,
  onShowPayment,
  onRecordManualPayment,
}: OrdreActionsMenuProps) {
  const isModal = context === "modal";

  const handleToggleFlag = async (flag: OrdreFlag) => {
    try {
      const currentFlags = ordre.flags || [];
      const isActive = currentFlags.includes(flag);
      const newFlags = isActive ? currentFlags.filter((f) => f !== flag) : [...currentFlags, flag];
      if (!isActive && !ordre.flag_reason) {
        onRequestFlagReason(flag, newFlags as OrdreFlag[]);
        return;
      }
      const updated = await ordresApi.update(ordre.id, {
        flags: newFlags,
        ...(newFlags.length === 0 ? { flag_reason: "" } : {}),
      } as any);
      useOrdreStore.getState().updateOrdre(updated);
      showNotification({ type: "success", message: isActive ? "Drapeau retiré" : `Drapeau « ${ORDRE_FLAG_CONFIG[flag].label} » ajouté` });
    } catch {
      showNotification({ type: "error", message: "Erreur" });
    }
  };

  const handleToggleCarteGriseRequired = async () => {
    try {
      const next = !ordre.carte_grise_not_required;
      const updated = await ordresApi.update(ordre.id, { carte_grise_not_required: next } as any);
      useOrdreStore.getState().updateOrdre(updated);
      showNotification({
        type: "success",
        message: next
          ? "Dépôt de la carte grise masqué pour le client"
          : "Dépôt de la carte grise réaffiché pour le client",
      });
    } catch {
      showNotification({ type: "error", message: "Erreur" });
    }
  };

  return (
    <MenuButton label="Actions" closeRef={closeRef}>
      <div className="grid grid-cols-2" style={{ minWidth: 520 }}>
        {/* ── Column 1 ── */}
        <div className="flex flex-col">
          <div className="flex flex-col p-1">
            <div className="text-h2 text-neutral-400 p-2">ACTIONS RAPIDES</div>
            {!isModal && (
              <SideMenuButton size="large" label="Modifier l'ordre" name="modify" onClick={onEdit} />
            )}
            <SideMenuButton size="large" label="Supprimer l'ordre" name="delete" color="#ef4444" onClick={onDelete} />
            <div className="border-t border-neutral-100 my-1" />
            <div className="text-h2 text-neutral-400 p-2">DRAPEAUX</div>
            {(["bloque", "urgent"] as OrdreFlag[]).map((flag) => {
              const cfg = ORDRE_FLAG_CONFIG[flag];
              const isActive = (ordre.flags || []).includes(flag);
              return (
                <SideMenuButton
                  key={flag}
                  size="large"
                  label={isActive ? `Retirer « ${cfg.label} »` : cfg.label}
                  name={cfg.icon}
                  color={isActive ? cfg.color : undefined}
                  onClick={() => handleToggleFlag(flag)}
                />
              );
            })}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">DOSSIER</div>
            {!isModal && ordre.dossier_id && (
              <SideMenuButton
                size="large"
                label="Voir le dossier"
                name="folder"
                onClick={() => navigateTo({ route: `/dossier/${ordre.dossier_id}` })}
              />
            )}
            {ordre.devis_sign_status === "pending" ? (
              <SideMenuButton
                size="large"
                label="Copier le lien de signature"
                name="link"
                onClick={() => {
                  if (ordre.devis_sign_token) {
                    const url = `${window.location.origin}/sign/${ordre.devis_sign_token}`;
                    navigator.clipboard.writeText(url);
                    showNotification({ type: "success", message: "Lien de signature copié" });
                  }
                }}
              />
            ) : ordre.devis_sign_status === "signed" ? (
              <SideMenuButton size="large" label="Devis signé" name="done2" color="#059669" />
            ) : null}
            {ordre?.intervention_groups && Object.values(ordre.intervention_groups).some((g) => g.devis_token) && (
              <SideMenuButton size="large" label="Rappels devis" name="cloche" onClick={onShowReminder} />
            )}
            <SideMenuButton size="large" label="Ajouter un document" name="upload" onClick={onAddDocument} />
            {hasFacture ? (
              <SideMenuButton size="large" label="Facture ajoutée" name="done2" color="#059669" />
            ) : (
              <SideMenuButton size="large" label="Ajouter la facture" name="file" onClick={onAddFacture} />
            )}
          </div>
          {orgData?.servicebox?.enabled && (
            <div className="flex flex-col border-t-1 p-1">
              <div className="text-h2 text-neutral-400 p-2">SERVICE BOX</div>
              <SideMenuButton
                size="large"
                label={ordre.or_number ? `OR n°${ordre.or_number}` : "Transférer vers ServiceBox"}
                name="sett-servicebox"
                color={ordre.or_number ? "#059669" : undefined}
                disabled={!serviceboxProxyAvailable}
                onClick={onServiceBoxTransfer}
              />
              <SideMenuButton
                size="large"
                label={sbEstimationLoading ? "Récupération..." : "Récupérer devis"}
                name="import-devis"
                disabled={!serviceboxProxyAvailable || !ordre.or_number || sbEstimationLoading}
                onClick={onImportEstimation}
              />
              {ordre.or_number && (
                <>
                  {ordre.alpha_or_number && (
                    <SideMenuButton size="large" label={`Alpha DMS OR n°${ordre.alpha_or_number}`} name="sett-servicebox" color="#059669" disabled />
                  )}
                  {ordre.servicebox_dossier_id && (
                    <SideMenuButton
                      size="large"
                      label="Voir le Dossier ServiceBox"
                      name="side-reception"
                      onClick={() => {
                        window.open(
                          `https://servicebox.mpsa.com/panier/panierSetCurrent.do?id=${ordre.servicebox_dossier_id}`,
                          "_blank",
                        );
                      }}
                    />
                  )}
                  {!ordre.servicebox_rdv_id && (
                    <SideMenuButton
                      size="large"
                      label="Re-transférer vers ServiceBox"
                      name="sett-servicebox"
                      disabled={!serviceboxProxyAvailable}
                      onClick={onServiceBoxTransfer}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {/* ── Column 2 ── */}
        <div className="flex flex-col border-l border-neutral-200">
          <div className="flex flex-col p-1">
            <div className="text-h2 text-neutral-400 p-2">PLANNING / RDV</div>
            <SideMenuButton size="large" label="Ajouter un RDV secondaire" name="plus" onClick={onShowSecondary} />
            {!isModal && ordre.reception_date && (
              <SideMenuButton
                size="large"
                label="Voir dans planning réception"
                name="side-reception"
                onClick={() => {
                  const rd = ordre.reception_date;
                  if (!rd) return;
                  const d = new Date(rd.seconds * 1000);
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  usePlanningStore.getState().setSelectedDate(iso);
                  usePlanningStore.getState().setHighlightOrdreId(ordre.id);
                  navigateTo({ route: "/reception" });
                }}
              />
            )}
            {!isModal && (
              <SideMenuButton
                size="large"
                label="Voir dans planning atelier"
                name="side-atelier"
                onClick={() => {
                  const jobs = useAtelierStore.getState().jobs.filter((j) => j.ordre_id === ordre.id);
                  if (jobs.length > 0) {
                    useAtelierStore.getState().setSelectedDate(jobs[0].date);
                  } else if (ordre.reception_date) {
                    const d = new Date(ordre.reception_date.seconds * 1000);
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    useAtelierStore.getState().setSelectedDate(iso);
                  }
                  useAtelierStore.getState().setHighlightOrdreId(ordre.id);
                  navigateTo({ route: "/atelier" });
                }}
              />
            )}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">VÉHICULE DE REMPLACEMENT</div>
            {ordre.mobility_option && !ordre.rental_vehicle_id && (
              <div
                className="flex flex-row items-center gap-2 px-3 py-2 mx-1 mb-1 rounded-lg"
                style={{
                  background: ordre.mobility_option === "attente" ? "#F3F4F6" : "#FEF3C7",
                  border: `1px solid ${ordre.mobility_option === "attente" ? "#D1D5DB" : "#FDE68A"}`,
                }}
              >
                <Icon1 name={ordre.mobility_option === "attente" ? "horloge" : "info"} size={14} color={ordre.mobility_option === "attente" ? "#6B7280" : "#92400E"} />
                <span className="text-h4 font-medium" style={{ color: ordre.mobility_option === "attente" ? "#6B7280" : "#92400E" }}>
                  {ordre.mobility_option === "attente" ? "Client en attente sur place" : `Demande : ${MOBILITY_LABELS[ordre.mobility_option] || ordre.mobility_option}`}
                </span>
              </div>
            )}
            {ordre.rental_vehicle_id ? (
              <>
                <SideMenuButton
                  size="large"
                  label={rentalVehicle ? `${rentalVehicle.name} (${rentalVehicle.plate})` : "Véhicule attribué"}
                  name="done2"
                  color="#059669"
                  onClick={ordre.rental_reservation_id ? onEditReservation : undefined}
                />
                <SideMenuButton size="large" label="Changer le véhicule" name="restart" onClick={onChangeRental} />
                <SideMenuButton size="large" label="Retirer le véhicule" name="delete" color="#ef4444" onClick={onRemoveRental} />
                <SideMenuButton size="large" label="Générer contrat de prêt" name="document" color="#6366f1" onClick={onShowContrat} />
              </>
            ) : (
              <SideMenuButton size="large" label="Attribuer un véhicule" name="side-vehicules" onClick={onShowRental} />
            )}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">CLIENT</div>
            <SideMenuButton size="large" label="Voir le suivi client" name="eye" onClick={onShowTracking} />
            <SideMenuButton
              size="large"
              label={ordre.carte_grise_not_required ? "Réafficher le dépôt carte grise" : "Masquer le dépôt carte grise"}
              name="document"
              color={ordre.carte_grise_not_required ? "#059669" : undefined}
              onClick={handleToggleCarteGriseRequired}
            />
            <SideMenuButton
              size="large"
              label="Copier le lien de suivi"
              name="link"
              onClick={() => {
                const url = `${window.location.origin}/tracking/${ordre.id}`;
                navigator.clipboard.writeText(url);
                showNotification({ type: "success", message: "Lien copié !" });
              }}
            />
            {!payments.some((p) => p.type === "acompte" && !["cancelled", "error"].includes(p.state)) && (
              <SideMenuButton size="large" label="Demander un acompte" name="depot" onClick={() => onShowPayment("acompte")} />
            )}
            {!payments.some((p) => p.type === "solde" && !["cancelled", "error"].includes(p.state)) &&
              !payments.some((p) => p.type === "acompte" && !["cancelled", "error", "paid"].includes(p.state)) && (
                <SideMenuButton size="large" label="Demander le solde" name="recup-argent" onClick={() => onShowPayment("solde")} />
              )}
            {onRecordManualPayment && (
              <SideMenuButton size="large" label="Enregistrer un paiement" name="payments" onClick={onRecordManualPayment} />
            )}
          </div>
        </div>
      </div>
    </MenuButton>
  );
}
